import { Tabs, Tag } from 'antd';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLifecycles } from 'react-use';

import { PageHeader } from '@ant-design/pro-components';

import { ApplicationStatusEnum } from '../../../enum/applicationStepStatus';
import {
  getPendingIslamicApplicationsThunk,
  getReferbackIslamicApplicationsThunk,
  getReferbackFromPendingDisbursementIslamicApplicationsThunk,
  getFollowUpIslamicApplicationsThunk,
  getRejectedIslamicApplicationsThunk,
  getCancelledIslamicApplicationsThunk,
} from '../../../services/islamicApplicationService/islamicApplicationThunk';
import { AppDispatch, RootState } from '../../../store/store';
import CancelledIslamicApplicationListing from './CancelledApplicationListing';
import FollowUpIslamicApplicationListing from './FollowUpApplicationListing';
import PendingIslamicApplicationListing from './PendingApplicationListing';
import ReferBackIslamicApplicationListing from './ReferBackApplicationListing';
import ReferBackFromPendingDisbursementIslamicApplicationListing from './ReferBackFromPendingDisbursementApplicationListing';
import RejectedIslamicApplicationListing from './RejectedApplicationListing';

import type { TabsProps } from 'antd';

const VerifyingIslamicApplicationListingPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  // const [title, setTitle] = useState("Pending");
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const activeTab = queryParams.get('tab');
  const pageNum = queryParams.get('pageNum');
  const pageSize = queryParams.get('pageSize');

  const [searchFiled, setSearchFiled] = useState('applicationNo');

  const {
    pendingIslamicApplicationList,
    referBackIslamicApplicationList,
    referBackFromPendingDisbursementIslamicApplicationList,
    followUpIslamicApplicationList,
    rejectIslamicApplicationList,
    cancelledIslamicApplicationList,
  } = useSelector((state: RootState) => state.islamicApplication);

  const initialParams = useMemo(
    () => ({
      pagination: {
        current: 1,
        pageSize: 20,
      },
      sortOrder: 'desc',
      sortField: 'submissionDate',
      search: {
        [searchFiled]: '',
      },
      filters: {},
    }),
    []
  );

  const items: TabsProps['items'] = [
    {
      key: 'pending',
      label: (
        <span>
          Pending{' '}
          {pendingIslamicApplicationList?.data?.searchTotal != null && (
            <Tag>{pendingIslamicApplicationList?.data?.total}</Tag>
          )}
        </span>
      ),
      children: <PendingIslamicApplicationListing />,
    },
    {
      key: 'referBack',
      label: (
        <span>
          Refer-back{' '}
          {referBackIslamicApplicationList?.data?.searchTotal != null && (
            <Tag>{referBackIslamicApplicationList?.data?.searchTotal}</Tag>
          )}
        </span>
      ),
      children: <ReferBackIslamicApplicationListing />,
    },
    {
      key: 'referBackFromPendingDisbursement',
      label: (
        <span>
          Refer-back from Pending Disbursement{' '}
          {referBackFromPendingDisbursementIslamicApplicationList?.data?.searchTotal != null && (
            <Tag>{referBackFromPendingDisbursementIslamicApplicationList?.data?.total}</Tag>
          )}
        </span>
      ),
      children: <ReferBackFromPendingDisbursementIslamicApplicationListing />,
    },
    {
      key: 'followUp',
      label: (
        <span>
          Follow Up{' '}
          {followUpIslamicApplicationList?.data?.searchTotal != null && (
            <Tag>{followUpIslamicApplicationList?.data?.total}</Tag>
          )}
        </span>
      ),
      children: <FollowUpIslamicApplicationListing />,
    },
    {
      key: 'rejected',
      label: (
        <span>
          Rejected{' '}
          {rejectIslamicApplicationList?.data?.searchTotal != null && (
            <Tag>{rejectIslamicApplicationList?.data?.total}</Tag>
          )}
        </span>
      ),
      children: <RejectedIslamicApplicationListing />,
    },
    {
      key: 'cancelled',
      label: (
        <span>
          Cancelled{' '}
          {cancelledIslamicApplicationList?.data?.searchTotal != null && (
            <Tag>{cancelledIslamicApplicationList?.data?.total}</Tag>
          )}
        </span>
      ),
      children: <CancelledIslamicApplicationListing />,
    },
  ];

  const onFetchApplications = (status: ApplicationStatusEnum, params: any) => {
    if (status === ApplicationStatusEnum.PENDING) {
      dispatch(getPendingIslamicApplicationsThunk(params));
    }
    if (status === ApplicationStatusEnum.REFERBACK) {
      dispatch(getReferbackIslamicApplicationsThunk(params));
    }
    if (status === ApplicationStatusEnum.FOLLOWED_UP) {
      dispatch(getFollowUpIslamicApplicationsThunk(params));
    }
    if (status === ApplicationStatusEnum.REJECTED) {
      dispatch(getRejectedIslamicApplicationsThunk(params));
    }
    if (status === ApplicationStatusEnum.REFERBACK_FROM_PENDING_DISBURSEMENT) {
      dispatch(getReferbackFromPendingDisbursementIslamicApplicationsThunk(params));
    }
    if (status === ApplicationStatusEnum.CANCELED) {
      dispatch(getCancelledIslamicApplicationsThunk(params));
    }
  };

  const onTabChange = (key: string) => {
    if (key === 'pending') {
      onFetchApplications(ApplicationStatusEnum.PENDING, initialParams);
    } else if (key === 'referBack') {
      onFetchApplications(ApplicationStatusEnum.REFERBACK, initialParams);
    } else if (key === 'followUp') {
      onFetchApplications(ApplicationStatusEnum.FOLLOWED_UP, initialParams);
    } else if (key === 'rejected') {
      onFetchApplications(ApplicationStatusEnum.REJECTED, initialParams);
    } else if (key === 'referBackFromPendingDisbursement') {
      onFetchApplications(ApplicationStatusEnum.REFERBACK_FROM_PENDING_DISBURSEMENT, initialParams);
    } else if (key === 'cancelled') {
      onFetchApplications(ApplicationStatusEnum.CANCELED, initialParams);
    }

    queryParams.set('tab', key);
    queryParams.set('pageNum', `${initialParams.pagination.current}`);
    queryParams.set('pageSize', `${initialParams.pagination.pageSize}`);

    const newSearch = queryParams.toString();

    navigate({
      pathname: location.pathname,
      search: newSearch,
    });
  };

  useLifecycles(() => {
    const params = {
      ...initialParams,
      pagination: {
        current: pageNum ? +pageNum : initialParams.pagination.current,
        pageSize: pageSize ? +pageSize : initialParams.pagination.pageSize,
      },
    };

    onFetchApplications(ApplicationStatusEnum.PENDING, params);
    onFetchApplications(ApplicationStatusEnum.REFERBACK, params);
    onFetchApplications(ApplicationStatusEnum.FOLLOWED_UP, params);
    onFetchApplications(ApplicationStatusEnum.REJECTED, params);
    onFetchApplications(ApplicationStatusEnum.CANCELED, params);
    onFetchApplications(ApplicationStatusEnum.REFERBACK_FROM_PENDING_DISBURSEMENT, params);
  });

  return (
    <>
      <Helmet>
        <title>{`Application Listing - redCASH CEP`}</title>
      </Helmet>
      <PageHeader className="px-0 mb-2" title="Verifying Application" />
      <Tabs defaultActiveKey={activeTab ?? 'pending'} items={items} onChange={onTabChange} />
    </>
  );
};

export default VerifyingIslamicApplicationListingPage;
